import Dexie, { type EntityTable } from 'dexie';
import type {
  AddOnRecord,
  AuditLogRecord,
  CustomerRecord,
  OrderItemRecord,
  OrderRecord,
  OutboxRecord,
  PaymentRecord,
  ServiceRecord,
  ServiceItemRecord,
  SettingRecord,
  ShiftRecord,
  SyncMetadataRecord,
  SystemMetadata,
} from './types';

/**
 * BABS local-first database.
 *
 * Version 1 is retained as the original Phase 0 foundation.
 * Version 2 adds the complete Phase 3 operational schema.
 */
export class BabsDatabase extends Dexie {
  systemMetadata!: EntityTable<SystemMetadata, 'key'>;
  orders!: EntityTable<OrderRecord, 'id'>;
  orderItems!: EntityTable<OrderItemRecord, 'id'>;
  customers!: EntityTable<CustomerRecord, 'id'>;
  payments!: EntityTable<PaymentRecord, 'id'>;
  services!: EntityTable<ServiceRecord, 'id'>;
  serviceItems!: EntityTable<ServiceItemRecord, 'id'>;
  addons!: EntityTable<AddOnRecord, 'id'>;
  outbox!: EntityTable<OutboxRecord, 'id'>;
  auditLogs!: EntityTable<AuditLogRecord, 'id'>;
  settings!: EntityTable<SettingRecord, 'key'>;
  syncMetadata!: EntityTable<SyncMetadataRecord, 'key'>;
  shifts!: EntityTable<ShiftRecord, 'id'>;

  constructor() {
    super('BabsPOSDB');

    this.version(1).stores({
      systemMetadata: 'key',
    });

    this.version(2).stores({
      systemMetadata: 'key',
      orders:
        'id, &orderNumber, &idempotencyKey, customerId, orderStatus, claimCode, paymentStatus, createdAt',
      orderItems: 'id, orderId, serviceId, createdAt',
      customers: 'id, &customerCode, mobileNumber, name, updatedAt',
      payments: 'id, orderId, method, createdAt',
      services: 'id, sortOrder, pricingType',
      addons: 'id, sortOrder, pricingType',
      outbox: 'id, aggregateId, &idempotencyKey, status, createdAt',
      auditLogs: 'id, entityId, action, createdAt',
      settings: 'key',
      syncMetadata: 'key',
      shifts: 'id, cashierId, deviceId, status, openedAt',
    });

    this.version(3).stores({
      systemMetadata: 'key',
      orders:
        'id, &orderNumber, &idempotencyKey, customerId, orderStatus, claimCode, paymentStatus, createdAt',
      orderItems: 'id, orderId, serviceId, createdAt',
      customers: 'id, &customerCode, mobileNumber, name, updatedAt',
      payments: 'id, orderId, method, createdAt',
      services: 'id, sortOrder, pricingType',
      serviceItems: 'id, serviceId, sortOrder, active',
      addons: 'id, sortOrder, pricingType',
      outbox: 'id, aggregateId, &idempotencyKey, status, createdAt',
      auditLogs: 'id, entityId, action, createdAt',
      settings: 'key',
      syncMetadata: 'key',
      shifts: 'id, cashierId, deviceId, status, openedAt',
    });

    this.version(4).stores({
      systemMetadata: 'key',
      orders:
        'id, &orderNumber, &idempotencyKey, customerId, orderStatus, claimCode, paymentStatus, createdAt, claimedAt, claimedBy',
      orderItems: 'id, orderId, serviceId, createdAt',
      customers: 'id, &customerCode, mobileNumber, name, updatedAt',
      services: 'id, sortOrder, pricingType',
      serviceItems: 'id, serviceId, sortOrder, active',
      addons: 'id, sortOrder, pricingType',
      payments: 'id, orderId, method, createdAt',
      outbox: 'id, aggregateId, &idempotencyKey, status, createdAt',
      auditLogs: 'id, entityId, action, createdAt',
      settings: 'key',
      syncMetadata: 'key',
      shifts: 'id, cashierId, deviceId, status, openedAt',
    });

    this.version(5)
      .stores({
        systemMetadata: 'key',
        orders:
          'id, &orderNumber, &idempotencyKey, customerId, orderStatus, claimCode, paymentStatus, createdAt, claimedAt, claimedBy',
        orderItems: 'id, orderId, serviceId, createdAt',
        customers: 'id, &customerCode, mobileNumber, name, updatedAt',
        services: 'id, sortOrder, pricingType',
        serviceItems: 'id, serviceId, sortOrder, active',
        addons: 'id, sortOrder, pricingType',
        outbox: 'id, aggregateId, &idempotencyKey, status, createdAt',
        auditLogs: 'id, entityId, action, createdAt',
        settings: 'key',
        syncMetadata: 'key',
        shifts: 'id, cashierId, deviceId, status, openedAt',
      })
      .upgrade(async (tx) => {
        await tx
          .table('outbox')
          .toCollection()
          .modify((record: { status?: string }) => {
            if (record.status === 'PROCESSING') record.status = 'PENDING';
          });
      });

    this.version(6).stores({
      systemMetadata: 'key',
      orders:
        'id, &orderNumber, &idempotencyKey, customerId, orderStatus, claimCode, paymentStatus, createdAt, claimedAt, claimedBy',
      orderItems: 'id, orderId, serviceId, createdAt',
      customers: 'id, &customerCode, mobileNumber, name, updatedAt',
      services: 'id, sortOrder, pricingType',
      serviceItems: 'id, serviceId, sortOrder, active',
      addons: 'id, sortOrder, pricingType',
      payments: 'id, orderId, method, createdAt',
      outbox: 'id, aggregateId, &idempotencyKey, status, createdAt',
      auditLogs: 'id, entityId, action, createdAt',
      settings: 'key',
      syncMetadata: 'key',
      shifts: 'id, cashierId, deviceId, status, openedAt, closedAt',
    });

    this.version(7).stores({
      systemMetadata: 'key',
      orders:
        'id, &orderNumber, &idempotencyKey, customerId, cashierId, orderStatus, claimCode, paymentStatus, createdAt, claimedAt, claimedBy',
      orderItems: 'id, orderId, serviceId, createdAt',
      customers: 'id, &customerCode, mobileNumber, name, updatedAt',
      services: 'id, sortOrder, pricingType',
      serviceItems: 'id, serviceId, sortOrder, active',
      addons: 'id, sortOrder, pricingType',
      payments: 'id, orderId, method, createdAt',
      outbox: 'id, aggregateId, &idempotencyKey, status, createdAt',
      auditLogs: 'id, entityId, action, createdAt',
      settings: 'key',
      syncMetadata: 'key',
      shifts: 'id, cashierId, deviceId, status, openedAt, closedAt',
    });
  }
}

export const db = new BabsDatabase();
